import { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import { Container, Box, Typography } from '@mui/material';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import './trasparenza-ia.scss';

gsap.registerPlugin(ScrollTrigger);

function TrasparenzaIA() {
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const animateSplitText = (element: HTMLElement) => {
    const text = element.textContent || '';
    element.innerHTML = text
      .split('')
      .map(char => char === ' ' ? ' ' : `<span class="split-char">${char}</span>`)
      .join('');

    const chars = element.querySelectorAll('.split-char');
    gsap.fromTo(chars,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.6,
        stagger: 0.03,
        ease: "power3.out",
        scrollTrigger: {
          trigger: element,
          start: "top 80%",
          toggleActions: "play none none none"
        }
      }
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (heroRef.current) {
        gsap.fromTo(heroRef.current,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 1, ease: "power2.out" }
        );
      }
      if (titleRef.current) {
        animateSplitText(titleRef.current);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Trasparenza IA | Fabbri Agricoltura</title>
        <meta name="description" content="Dichiarazione di trasparenza sull'uso dell'Intelligenza Artificiale nel sito Fabbri Agricoltura, ai sensi dell'art. 50 Reg. UE 2024/1689 (AI Act)." />
      </Helmet>

      <Box className="legal-page-agricola" id="top">
        {/* Hero */}
        <Box className="legal-hero-agricola" ref={heroRef}>
          <Container maxWidth="lg" className="legal-hero-content">
            <Typography variant="h1" className="legal-hero-title split-text" ref={titleRef}>
              Trasparenza sull'uso dell'Intelligenza Artificiale
            </Typography>
            <Typography className="legal-hero-subtitle">
              Ai sensi dell'articolo 50 del Regolamento UE 2024/1689 (AI Act)
            </Typography>
          </Container>
        </Box>

        {/* Content */}
        <Box className="legal-content-section">
          <Container maxWidth="lg">

            {/* 1. Dichiarazione generale */}
            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">1.</span> Dichiarazione generale
              </Typography>
              <Typography className="legal-text">
                Fabbri Agricoltura dichiara che alcune immagini e video presenti su questo sito sono stati generati o elaborati con l'ausilio di strumenti di intelligenza artificiale, ai sensi dell'articolo 50 del Regolamento UE 2024/1689 (AI Act). Questa pagina garantisce la massima trasparenza identificando tutti i contenuti prodotti o modificati tramite sistemi di IA.
              </Typography>
            </Box>

            {/* 2. Contenuti video generati con IA */}
            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">2.</span> Contenuti video generati con IA
              </Typography>
              <Typography className="legal-text">
                I seguenti video decorativi, utilizzati come sfondo nelle sezioni hero del sito, sono stati generati tramite intelligenza artificiale:
              </Typography>
              <ul className="legal-list">
                <li><strong>video1.mp4</strong> - Sfondo hero Homepage (paesaggio agricolo)</li>
                <li><strong>video2.mp4</strong> - Sfondo hero pagina Servizi</li>
                <li><strong>video3.mp4</strong> - Sfondo hero pagina Contatti</li>
                <li><strong>video4.mp4</strong> - Sfondo hero pagina Prodotti</li>
              </ul>
              <Typography className="legal-text legal-note">
                <strong>Nota:</strong> questi video hanno funzione esclusivamente decorativa e ambientale. Non rappresentano riprese reali dell'attività aziendale e non intendono ingannare circa la natura dei servizi offerti.
              </Typography>
            </Box>

            {/* 3. Tipologia di contenuti e scopo */}
            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">3.</span> Tipologia di contenuti e scopo
              </Typography>
              <Typography className="legal-text">
                <strong>Tipo di contenuto:</strong> video decorativi ambientali per sezioni hero delle pagine del sito.
              </Typography>
              <Typography className="legal-text">
                <strong>Natura:</strong> interamente generati tramite intelligenza artificiale; non rappresentano riprese video reali di persone, luoghi o macchinari reali.
              </Typography>
              <Typography className="legal-text">
                <strong>Scopo:</strong> funzione illustrativa e decorativa per migliorare l'esperienza di navigazione. Non intendono ingannare circa la natura dei servizi agricoli offerti.
              </Typography>
            </Box>

            {/* 4. Contenuti NON generati con IA */}
            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">4.</span> Contenuti non generati con IA
              </Typography>
              <Typography className="legal-text">
                I seguenti contenuti presenti sul sito sono reali e <strong>non</strong> generati con intelligenza artificiale:
              </Typography>
              <ul className="legal-list">
                <li>Loghi e immagini dei marchi partner (Pioneer, BASF, Bayer, Syngenta, Corteva, DEKALB, Adama, Agribios, Certis, FCP Cerea, ILSA, Lidea, Sumitomo, UPL, Yara)</li>
                <li>Video promozionali dei marchi (contenuti ufficiali dei produttori)</li>
                <li>Logo Fabbri Agricoltura</li>
                <li>Tutti i testi e le descrizioni dei servizi</li>
              </ul>
            </Box>

            {/* 5. Accessibilità e attributi */}
            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">5.</span> Accessibilità e attributi
              </Typography>
              <Typography className="legal-text">
                Tutti i video generati con IA riportano nell'attributo <code>aria-label</code> la dicitura "Video decorativo generato con intelligenza artificiale", informando gli utenti che utilizzano tecnologie assistive (screen reader) della natura del contenuto.
              </Typography>
            </Box>

            {/* 6. Riferimenti normativi */}
            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">6.</span> Riferimenti normativi
              </Typography>
              <ul className="legal-list">
                <li>Regolamento UE 2024/1689 (AI Act), articolo 50 - obblighi di trasparenza per sistemi di IA generativa</li>
                <li>Regolamento UE 2016/679 (GDPR) - trasparenza nel trattamento dei dati</li>
              </ul>
            </Box>

            {/* 7. Aggiornamenti */}
            <Box className="legal-block">
              <Typography variant="h2" className="legal-section-title">
                <span className="section-number">7.</span> Aggiornamenti
              </Typography>
              <Typography className="legal-text">
                Questa pagina verrà aggiornata ogni volta che nuovi contenuti generati con IA verranno aggiunti al sito.
              </Typography>
            </Box>

            {/* Footer info */}
            <Box className="legal-block legal-footer-info">
              <Typography className="legal-text">
                <strong>Fabbri Agricoltura</strong><br />
                <a href="https://maps.app.goo.gl/ws26E8mfGKWDVsZL9" target="_blank" rel="noopener noreferrer">Via Per S. Felice, 3 - 41031 Camposanto (MO)</a><br />
                Email: <a href="mailto:fabbri.agricoltura@outlook.it">fabbri.agricoltura@outlook.it</a>
              </Typography>
              <Typography className="legal-text legal-update">
                Ultimo aggiornamento: agosto 2026
              </Typography>
            </Box>

          </Container>
        </Box>
      </Box>
    </>
  );
}

export default TrasparenzaIA;
